import store from '../index';
import { Broadcast, BareUser } from './event/types';
import { moveToEndEvent } from './event/utils';
import { SERVER_CLIENT_COMMUNICATION_MODE } from './mattermost';

import type { AiSummaryStatusResponse } from '@goldcast/api/backend';

import { AGORA, MATTERMOST_DEFAULTS } from '@/constants';
import { handleSocketReactionPayload } from '@/services/floatingEmoji.service';
import loggerService from '@/services/logger.service';
import {
  showAskToUnmuteDialog,
  showKickedFromStageDialog,
  showMutedByOrganizerDialog,
  showUnmutedByOrganizerDialog
} from '@/services/stageControl.service';
import { debounceUpdateTextQADetails, resetModeratePosts } from '@/services/textQNA.service';
import { getCurrentRoute, replaceRoute } from '@/utils/routerAccess';
import { hasSocketSessionMarker, setSocketSessionMarker } from '@/utils/socketSessionMarker';
import { makeUser } from '@/utils/userTransform';

//stale Uber data detection function
function detectStaleBroadcastData(storeBroadcasts: Broadcast[], newUberBroadcasts: Broadcast[]): void {
  if (!storeBroadcasts?.length || !newUberBroadcasts?.length) {
    return;
  }

  newUberBroadcasts.forEach(uberBroadcast => {
    const storeBroadcast = storeBroadcasts.find(b => b.id === uberBroadcast.id);
    if (!storeBroadcast?.updated_at || !uberBroadcast.updated_at) {
      return;
    }

    if (new Date(uberBroadcast.updated_at) < new Date(storeBroadcast.updated_at)) {
      loggerService.error('STALE_DATA: Uber broadcast data is stale', {
        broadcastId: uberBroadcast.id,
        storeUpdatedAt: storeBroadcast.updated_at,
        uberUpdatedAt: uberBroadcast.updated_at
      });
    }
  });
}

export type SocketEventType =
  | 'broadcast-updated'
  | 'broadcast-user-ranks-updated'
  | 'survey-updated'
  | 'booth-updated'
  | 'booth-added'
  | 'event-updated'
  | 'event-speakers-updated'
  | 'agenda-item-updated'
  | 'public-user-updated'
  | 'you-are-blocked'
  | 'you-are-deleted'
  | 'user-blocked'
  | 'mute-speaker-audio'
  | 'mute-speaker-video'
  | 'ask-to-unmute'
  | 'unmute-speaker-audio'
  | 'unmute-speaker-video'
  | 'remove-speaker-from-stage'
  | 'watcher-count-updated'
  | 'watcher_count'
  | 'user-permission-updated'
  | 'event-staffs-updated'
  | 'cue-updated'
  | 'floating_emoji_reactions'
  | 'ai-summary-completed';

export const UBER_RELATED_SOCKET_EVENTS: SocketEventType[] = [
  'agenda-item-updated',
  'booth-added',
  'booth-updated',
  'broadcast-updated',
  'event-speakers-updated',
  'event-staffs-updated',
  'event-updated'
];

const socketMessageTracker: { [k: string]: any } = {};

export function getSocketMsgTrackerKey(eventType: SocketEventType, data: any) {
  switch (eventType) {
    case 'broadcast-updated': {
      return `${eventType}-${data.broadcast.id}`;
    }
    case 'broadcast-user-ranks-updated': {
      return `${eventType}-${data.broadcast?.broadcast_user?.[0]?.broadcast_id}`;
    }
    case 'booth-added':
    case 'booth-updated': {
      return `${eventType}-${data.booth.id}`;
    }
    case 'agenda-item-updated': {
      return `${eventType}-${data.agenda_item.id}`;
    }
    case 'cue-updated': {
      return `${eventType}-${data.cue?.broadcast_id || 'unknown'}`;
    }
    case 'event-updated':
    case 'event-speakers-updated':
    case 'public-user-updated':
    case 'survey-updated':
    case 'you-are-blocked':
    case 'you-are-deleted':
    case 'user-blocked':
    case 'mute-speaker-audio':
    case 'mute-speaker-video':
    case 'ask-to-unmute':
    case 'unmute-speaker-audio':
    case 'unmute-speaker-video':
    case 'remove-speaker-from-stage':
    case 'watcher-count-updated':
    case 'watcher_count':
    case 'user-permission-updated':
    case 'event-staffs-updated':
    case 'ai-summary-completed':
    case 'floating_emoji_reactions': {
      return eventType;
    }
  }
}

export const mappingToSocketProcessorHandler = {
  'broadcast-updated': processBroadcastUpdated,
  'broadcast-user-ranks-updated': processBroadcastUserRanksUpdated,
  'agenda-item-updated': processAgendaItemUpdated,
  'you-are-blocked': processUserBlocked,
  'user-blocked': processUserBlocked,
  'you-are-deleted': processUserDeleted,
  'event-updated': processEventUpdated,
  'public-user-updated': processPublicUserUpdated,
  'event-speakers-updated': processEventSpeakersUpdated,
  'booth-added': processBoothAdded,
  'booth-updated': processBoothUpdated,
  'mute-speaker-audio': processMuteSpeaker,
  'mute-speaker-video': processMuteSpeakerVideo,
  'ask-to-unmute': processAskToUnmuteSpeaker,
  'unmute-speaker-audio': processUnmuteSpeaker,
  'unmute-speaker-video': processUnmuteSpeakerVideo,
  'remove-speaker-from-stage': processRemoveSpeakerFromStage,
  'watcher-count-updated': processWatcherCountUpdated,
  'survey-updated': processSurveyUpdated,
  watcher_count: processWatcherCount,
  'user-permission-updated': processUserPermissionUpdated,
  'event-staffs-updated': processEventStaffsUpdated,
  'cue-updated': processCueUpdated,
  floating_emoji_reactions: processFloatingEmojiReactions,
  'ai-summary-completed': processAiSummaryCompleted
};

export function processSocketMessage(
  data: any,
  eventType: SocketEventType,
  source: string,
  forceSkipProcessing = false
) {
  const socketMsgTrackerKey = getSocketMsgTrackerKey(eventType, data) || '';
  let lastSavedEpoch = socketMessageTracker[socketMsgTrackerKey] || -1;
  const isUberRelatedSocketEvent = UBER_RELATED_SOCKET_EVENTS.includes(eventType);
  const lastUberFetchEpoch = store?.state?.event?.lastUberFetchEpoch;
  if (isUberRelatedSocketEvent && lastUberFetchEpoch > lastSavedEpoch && source === 'gatewaySockets') {
    lastSavedEpoch = lastUberFetchEpoch || -1;
    loggerService.info(
      `${source}: Got a uber related socket message. event-type:${eventType}, socketMessageEpoch: ${data.epoch} & lastUberFetchEpoch: ${lastSavedEpoch}`
    );
  }
  if (!forceSkipProcessing && (data.epoch || 0) > lastSavedEpoch) {
    socketMessageTracker[socketMsgTrackerKey] = data.epoch || -1;
    loggerService.info(`${source}: Processing socket message. event-type:${eventType}`);
    mappingToSocketProcessorHandler[eventType](data);
  } else {
    loggerService.warn(
      `${source}: Ignoring socket message as ${
        forceSkipProcessing ? 'mattermost was disabled' : 'it was already processed'
      } , event-type:${eventType}`
    );
  }
}

/************************************************/
// Socket message processors
/************************************************/

export function processEventUpdated(data: any) {
  const currentEventId = getCurrentRoute().params.eventID;
  const shouldProcessSocketMessage = currentEventId == data.event.id;
  if (!shouldProcessSocketMessage) {
    loggerService.info('Ignoring event update as event id does not match', {
      currentEventId,
      eventID: data.event.id
    });
    return;
  }

  // For Cvent events, theme_obj is sourced from the Cvent theme API (via applyCventThemeToUberEvent)
  // and merged in at initial load. The event-updated socket payload carries the Goldcast-side
  // theme_obj which would overwrite the Cvent theme if committed — so we strip it before setEvent.
  const isCventEvent = !!store.state.event.event.cvent_event_id;
  const { theme_obj: _omitted, ...eventWithoutTheme } = data.event;
  const eventData = isCventEvent ? { ...data, event: eventWithoutTheme } : data;

  store.commit('event/setEvent', eventData, { root: true });

  if (data.event.event_end_status === 'FORCE_ENDED') {
    moveToEndEvent(data.event.id);
    return;
  }

  store.commit('event/setThemeStyles', store.state);
  store.commit('event/avroom/updateJitterDelay', 10, { root: true });
  store.dispatch('event/checkBoothTabAndRedirect', data?.event, {
    root: true
  });
  if (data.event.speaker_stage_requests) {
    store.commit('event/setSpeakerStageRequests', data.event.speaker_stage_requests, { root: true });
  }
  if (data.event.resources) {
    store.commit('event/setResources', data.event.resources, { root: true });
  }
  if (
    'is_text_qna_moderated' in data.event &&
    data.event.is_text_qna_moderated !== store.state.event.textQNA?.textQnA?.isModerated
  ) {
    store.commit('event/textQNA/toggleTextQnaModeration', data.event.is_text_qna_moderated, { root: true });
    resetModeratePosts(data.event.is_text_qna_moderated);
    debounceUpdateTextQADetails();
  }
  if (
    ('textqna_answerers_alias' in data.event &&
      data.event.textqna_answerers_alias !== store.state.event.textQNA?.textQnA?.textQNAAnswerMeta?.answerersAlias) ||
    ('show_answerers_identity' in data.event &&
      data.event.show_answerers_identity !==
        store.state.event.textQNA?.textQnA?.textQNAAnswerMeta?.showAnswerersIdentity)
  ) {
    store.commit(
      'event/textQNA/updateTextQnAAnswerMeta',
      {
        answerersAlias: data.event?.textqna_answerers_alias || '',
        showAnswerersIdentity: data?.event.show_answerers_identity
      },
      { root: true }
    );
  }
  if (store.state.mattermost.socketstatus == 'bailed-noretry' && !store.getters['eventsource/shouldUseSSE']) {
    store.dispatch('mattermost/connect', null, { root: true });
  }
}

export function processBroadcastUpdated(data: any) {
  const currentEventId = store.state.event.event?.id ?? getCurrentRoute().params.eventID;
  if (currentEventId) {
    const broadcastEventId = data?.broadcast?.event;
    if (!broadcastEventId || broadcastEventId !== currentEventId) {
      loggerService.error('processBroadcastUpdated: dropping cross-event or untagged broadcast message', {
        broadcastEventId,
        currentEventId
      });
      return;
    }
  }
  store.dispatch('event/handleAgendaUpdate', { item: data && data.broadcast, isBroadcast: true }, { root: true });
}

type BroadcastUserRank = {
  user_id: string;
  stage_rank: number;
  broadcast_id?: string;
};

type BroadcastUserRanksUpdatedPayload = {
  broadcast: {
    broadcast_user: BroadcastUserRank[];
  };
};

export function processBroadcastUserRanksUpdated(data: BroadcastUserRanksUpdatedPayload) {
  const currentBroadcast = store.getters['event/currentBroadcast'] as Broadcast | undefined;
  if (!currentBroadcast) {
    return;
  }

  const broadcastUserRanks = data.broadcast.broadcast_user;
  const userRankMap = new Map<string, number>();
  broadcastUserRanks.forEach((item: BroadcastUserRank) => {
    userRankMap.set(item.user_id, item.stage_rank);
  });

  const updatedSpeakers = currentBroadcast.speakers.map((speaker: BareUser) => ({
    ...speaker,
    stage_rank: userRankMap.get(speaker.id)
  }));

  const updatedStaffDetails = ((currentBroadcast as any).staff_details || []).map((staff: BareUser) => ({
    ...staff,
    stage_rank: userRankMap.get(staff.id)
  }));

  store.dispatch(
    'event/handleAgendaUpdate',
    {
      item: {
        ...currentBroadcast,
        speakers: updatedSpeakers,
        staff_details: updatedStaffDetails
      },
      isBroadcast: true
    },
    { root: true }
  );
}

export function processBoothAdded(data: any) {
  store.commit('event/upsertBooth', data.booth, {
    root: true
  });

  for (const boothRoom of data.booth.discussion_group_objects) {
    store
      .dispatch('mattermost/getDebouncedChannelRefresher', boothRoom.mmid, {
        root: true
      })
      .then(refresherfcn => {
        refresherfcn();
      });
  }
}

export function processBoothUpdated(data: any) {
  store.commit('event/updateBoothsInPlace', data.booth, { root: true });
  for (const boothRoom of data.booth.discussion_group_objects) {
    store
      .dispatch('mattermost/getDebouncedChannelRefresher', boothRoom.mmid, {
        root: true
      })
      .then(refresherfcn => {
        refresherfcn();
      });
  }
}

export function processEventSpeakersUpdated(data: any) {
  store.dispatch(
    'event/setRawSpeakersFromUber',
    { users: data.event.speakers },
    {
      root: true
    }
  );
  if (store.state.bottomDrawer.title == 'Speakers') {
    (store.commit as any)('bottomDrawer/hide', null, {
      root: true
    });
  }
}

export function processAgendaItemUpdated(data: any) {
  store.dispatch('event/handleAgendaUpdate', { item: data && data.agenda_item }, { root: true });
}

export function processUserBlocked(data: any) {
  kickOut('Received socket message: ' + data.event);
}

export function processUserDeleted(data: any) {
  kickOut('Received socket message: ' + data.event);
}

export function processRemoveSpeakerFromStage(data: any) {
  logSocketSpeakerAction('speaker_demoted', data);
  showKickedFromStageDialog();
  store.commit('event/setShouldKickFromStage', true, {
    root: true
  });
}

export function processMuteSpeaker(data: any) {
  logSocketSpeakerAction('speaker_muted', data);
  showMutedByOrganizerDialog();
  const currentBroadcastId = store.state.route.params.id;
  if (currentBroadcastId) {
    const channelName =
      store.state.event.useNewMuteUnmuteCapabilities && !store.getters['mediaroom/isCurrentUserOnStage']
        ? currentBroadcastId + AGORA.UID_SUFFIX.BACKSTAGE
        : currentBroadcastId;
    store.commit(
      'mediaroom/setStageControl',
      {
        audio: false
      },
      { root: true }
    );
    store.dispatch(
      store.state.event.event.is_local_recording ? 'mediaroom/muteLocalTracks' : 'mediaroom/unpublishLocalTracks',
      {
        channelName,
        mediaTrackType: 'audio'
      },
      { root: true }
    );
  }
}

export function processAskToUnmuteSpeaker(data: any) {
  logSocketSpeakerAction('speaker_unmute_requested', data);
  showAskToUnmuteDialog();
}

export function processUnmuteSpeaker(data: any) {
  logSocketSpeakerAction('speaker_unmuted', data);
  showUnmutedByOrganizerDialog();
  const currentBroadcastId = store.state.route.params.id;
  if (currentBroadcastId) {
    const channelName =
      store.state.event.useNewMuteUnmuteCapabilities && !store.getters['mediaroom/isCurrentUserOnStage']
        ? currentBroadcastId + AGORA.UID_SUFFIX.BACKSTAGE
        : currentBroadcastId;
    store.commit(
      'mediaroom/setStageControl',
      {
        audio: true
      },
      { root: true }
    );
    store.dispatch('mediaroom/createLocalTrack', {
      channelName,
      mediaTrackType: 'audio',
      shouldMute: false
    });
  }
}

export function processMuteSpeakerVideo(data: any) {
  logSocketSpeakerAction('speaker_camera_disabled', data);
  updateRemoteCamera(false);
}

export function processUnmuteSpeakerVideo(data: any) {
  logSocketSpeakerAction('speaker_camera_enabled', data);
  updateRemoteCamera(true);
}

function updateRemoteCamera(enabled: boolean) {
  const currentBroadcastId = store.state.route.params.id;
  if (!currentBroadcastId) {
    return;
  }
  const channelName =
    store.state.event.useNewMuteUnmuteCapabilities && !store.getters['mediaroom/isCurrentUserOnStage']
      ? currentBroadcastId + AGORA.UID_SUFFIX.BACKSTAGE
      : currentBroadcastId;
  store.commit('mediaroom/setStageControl', { video: enabled }, { root: true });
  if (enabled) {
    store.dispatch('mediaroom/createLocalTrack', {
      channelName,
      mediaTrackType: 'video',
      shouldMute: false
    });
    return;
  }
  store.dispatch(
    store.state.event.event.is_local_recording ? 'mediaroom/muteLocalTracks' : 'mediaroom/unpublishLocalTracks',
    {
      channelName,
      mediaTrackType: 'video'
    },
    { root: true }
  );
}

function logSocketSpeakerAction(action: string, data: any) {
  try {
    const broadcastId = store.state.route.params.id;
    const metadata: Record<string, string> = {
      action,
      ...(typeof broadcastId === 'string' ? { broadcast_id: broadcastId } : {})
    };
    if (typeof data?.actor_id === 'string') {
      metadata.actor_id = data.actor_id;
    }
    if (typeof data?.target_id === 'string') {
      metadata.target_id = data.target_id;
    }
    loggerService.info('app_action', metadata);
  } catch {
    // Socket handling must continue even if LogRocket is unavailable.
  }
}

export function processUpdateConnectionStatus(payload: { status: string; firstConnectDone: boolean }) {
  const { status, firstConnectDone } = payload;
  let leaveChannel_p = Promise.resolve();
  if (status === 'online') {
    const event = store.state.event.event;
    const dgChannelsLoaded_p = new Promise((resolve, reject) => {
      setTimeout(
        () => {
          store
            .dispatch('mattermost/getDiscussionGroupWithoutLoader', event, { root: true })
            .then(resolve)
            .catch(reject);
        },
        (store.state.event.avroom?.jitterdelay || 0) * 1000
      );
    });
    if (!firstConnectDone) {
      // context.state.firstConnectDone = true;
      // We need to wait for dgChannels to be loaded so that we can check when leaving
      // channels if the user is there in a given channel or not
      leaveChannel_p = dgChannelsLoaded_p.then(() => {
        return store
          .dispatch('mattermost/leaveAllChannels', null, { root: true })
          .then(() => {
            return store
              .dispatch('event/avroom/joinChannelsForCurrentRoute', null, {
                root: true
              })
              .then(() => {
                sendRoomUpdateEvent();
              })
              .catch(err => {
                loggerService.error('Error in joining channels!');
              });
          })
          .catch(e => {
            // Supress this error because even if they fail to leave they should enter the event
            loggerService.error('error in updateSocketStatus', e);
          });
      });
    } else {
      loggerService.warn("First connect was already done, won't join channels again!");
      return store
        .dispatch('event/avroom/joinChannelsForCurrentRoute', ['offtopic', 'broadcast', 'booths'], {
          root: true
        })
        .catch(err => {
          loggerService.error('Error in joining channels!');
        });
    }

    // refetch notifications and DMs
    store
      .dispatch('event/avroom/getCurrentUserDMs', null, {
        root: true
      })
      .then(() => {
        store.dispatch('event/avroom/getTownhallMessages', null, {
          root: true
        });
      });
  }
  return leaveChannel_p;
}

export function sendRoomUpdateEvent() {
  const roomId = getCurrentRoom();
  if (!roomId) {
    loggerService.warn("mmsocket: Can't send room update event as roomId is not available");
    return;
  }
  if (store.state.mattermost.serverClientCommunicationMode == SERVER_CLIENT_COMMUNICATION_MODE.SSE) {
    store.dispatch('eventsource/updateRoomForSSE', roomId);
    return;
  }
  store.dispatch('mattermost/sendupdateRoomMsgForWebsocket', roomId);
}

export function getCurrentRoom() {
  const publicChannlMmid = store.state.mattermost.publicChannels.offtopic;
  const currentRoute = getCurrentRoute();
  if (!currentRoute?.name) {
    return null;
  }
  if (MATTERMOST_DEFAULTS.EXCLUDED_ROUTE_NAMES.includes(currentRoute.name.toString())) {
    return MATTERMOST_DEFAULTS.EXCLUDED_ROOM_ID;
  }
  if (currentRoute.fullPath?.includes('/broadcast') && currentRoute.params.id) {
    const broadcast: Broadcast = store.getters['event/currentEventBroadcast'](currentRoute.params.id);
    return broadcast?.mmid;
  }
  return publicChannlMmid;
}
export function processSocketOpenDone(isReconnect: boolean = false) {
  const getAgendaLivenessMap = () => {
    const broadcasts = store.state.event.event?.agenda?.broadcasts || [];

    return broadcasts.reduce((acc: Record<string, boolean>, item: any) => {
      if (item?.id) {
        acc[item.id] = item.is_live;
      }
      return acc;
    }, {});
  };

  const hasLivenessChanged = (
    previousBroadcast: Record<string, boolean>,
    currentBroadcast: Record<string, boolean>
  ) => {
    const previousBroadcastLivenessKeys = Object.keys(previousBroadcast);
    const currentBroadcastLivenessKeys = Object.keys(currentBroadcast);

    if (previousBroadcastLivenessKeys.length !== currentBroadcastLivenessKeys.length) {
      return true;
    }

    for (const key of previousBroadcastLivenessKeys) {
      if (previousBroadcast[key] !== currentBroadcast[key]) {
        return true;
      }
    }

    return false;
  };

  const livenessBeforeUpdate = getAgendaLivenessMap();
  const broadcastsBeforeUpdate = store.state.event.event?.agenda?.broadcasts || [];

  const eventID = getCurrentRoute().params.eventID as string;
  // A first open with the marker already set is a mid-event reload: socket messages sent while the page
  // reloaded are lost, so refetch uber. A true first entry has no marker and keeps the single uber call.
  const isReloadAfterConnect = !isReconnect && hasSocketSessionMarker(eventID);
  setSocketSessionMarker(eventID);

  store
    .dispatch('event/loadEventFromUber', {
      eventID,
      forceUpdate: !store.state.event.useEventApiConsolidation || isReconnect || isReloadAfterConnect
    })
    .then(() => {
      // Detect stale broadcast data after socket reconnection
      const broadcastsAfterUpdate = store.state.event.event?.agenda?.broadcasts || [];
      detectStaleBroadcastData(broadcastsBeforeUpdate, broadcastsAfterUpdate);

      const livenessAfterUpdate = getAgendaLivenessMap();
      const isLivenessChanged = hasLivenessChanged(livenessBeforeUpdate, livenessAfterUpdate);

      if (isLivenessChanged) {
        loggerService.info('Broadcast liveness changed - redirecting user');
        store.dispatch('event/stage/transitionOnSocketConnect', null, { root: true }).then(() => {
          store.dispatch('liveVideoRoom/handleAgendaVideoPlaybackState', null, { root: true });
        });
      } else {
        store.dispatch('liveVideoRoom/handleAgendaVideoPlaybackState', null, { root: true });
      }
    });

  // Validate user access after uber call
  if (getCurrentRoute().fullPath.startsWith('/e/')) {
    // Fetch current event and on 400 kickOut
    store.dispatch('apigateway/checkEventAndKick', 'Received 400 when processing SocketOpenDone').catch(() => {
      loggerService.warn('user is kicked out from the event!');
    });
  }
}

export function processPublicUserUpdated(data: any) {
  const userObj: any = {};
  const user = data?.public_user?.user;
  const madeUser = makeUser(user?.event_user);
  userObj[madeUser.id] = madeUser;
  store.commit('event/addUsersBulk', userObj, { root: true });
}

export function processSurveyUpdated(data: any) {
  store.dispatch('event/survey/handleSurveyUpdation', data?.survey, {
    root: true
  });
}

export function processWatcherCountUpdated(data: any) {
  store.commit('event/setActiveUsersCount', { [data.watcher_count.id]: data.watcher_count.count }, { root: true });
}

export function processWatcherCount(data: any) {
  store.commit('event/setActiveUsersCountUsingMmid', data, { root: true });
}

export function processUserPermissionUpdated(data: any) {
  store.dispatch('loadUserPermissions', {
    eventID: store.state.route.params.eventID,
    userId: store.state.user.currentUser?.id
  });
}

export function processEventStaffsUpdated(data: any) {
  const users = data.event.staffs.reduce((acc, staff) => {
    const madeUser = makeUser(staff);
    acc[madeUser.id] = madeUser;
    return acc;
  }, {});
  store.commit('event/addUsersBulk', users, { root: true });
}

export function processCueUpdated(data: any) {
  if (!data || typeof data !== 'object') {
    loggerService.warn('Invalid cue-updated socket payload', { data });
    return;
  }

  const socketBroadcastId = String(data?.cue?.broadcast_id || '');
  if (!socketBroadcastId) {
    loggerService.warn('No broadcast_id in cue-updated socket data', { data });
    return;
  }

  const currentBroadcast = store.getters['event/currentBroadcast'];
  const currentBroadcastId = String(currentBroadcast?.id || '');

  if (currentBroadcastId !== socketBroadcastId) {
    loggerService.info('Ignoring cue update as broadcast id does not match', {
      currentBroadcastId,
      socketBroadcastId
    });
    return;
  }

  const isManualMode = !!currentBroadcast?.manual_redirection_enabled;
  const canViewLiveRunOfShowCue = !!store.getters?.['isStaffMember'] || !!store.getters?.['event/isOrganizerOrSpeaker'];

  if (!canViewLiveRunOfShowCue) {
    loggerService.info('Skipping cue update fetch for attendee role', {
      broadcastId: currentBroadcastId
    });
    return;
  }

  const action = isManualMode ? 'event/loadLiveRunOfShowCuesWithSessionPreservation' : 'event/fetchLiveRunOfShowCues';

  store.dispatch(action, null, { root: true });
}

/************************************************/
// Helper functions
/************************************************/

function calculateLatencyAndPushToSegment(epoch: any) {
  if (!epoch) {
    return 0;
  }
  const currentTime: any = new Date().getTime();
  const latency = currentTime - epoch;
  return latency;
}

function kickOut(reason: string) {
  store.dispatch('mattermost/disconnect', null, { root: true });
  store.dispatch('apigateway/disconnect');
  loggerService.info('Kicking user out reason: ' + reason);
  store.commit('event/eventHeartbeat/stopEventHeartbeat');
  replaceRoute({
    name: 'ErrorPage403',
    query: {
      message: 'EVENT_ACCESS_NOT_ALLOWED'
    }
  });
}

export function processFloatingEmojiReactions(data: any) {
  if (!data?.emojis?.length) {
    return;
  }
  handleSocketReactionPayload(data);
}

export function processAiSummaryCompleted(data: any) {
  const aiSummary = data?.ai_summary;
  const socketTaskId: string | null = aiSummary?.task_id ?? null;
  const requestedTaskId: string | null = store.state.event.catchMeUpRequestedTaskId;

  if (!socketTaskId || socketTaskId !== requestedTaskId) {
    return;
  }

  const result: AiSummaryStatusResponse = {
    status: aiSummary?.status ?? 'failed',
    summary: aiSummary?.summary ?? null,
    error: aiSummary?.error ?? null
  };

  store.commit('event/setCatchMeUpSummary', result);
  store.commit('event/setCatchMeUpGeneratedAt', aiSummary?.generated_at ?? null);
}
